import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import html2pdf from 'html2pdf.js';

const AHUCalculator = () => {
    const [formData, setFormData] = useState({
        clientName: '',
        coilWMm: '',
        coilHMm: '',
        uvLampLMm: '',
        distance: '',
        airflowVolCfm: '',
        numRows: '',
        numCols: '',
        lampCount: '',
        totalWattage: '',
        rowWattage: '',
        intenAt1mMWcm2: '',
        uvWattageW: '',
        noOfLamps: '',
        distBtwnLamp: ''
    });

    const [calculations, setCalculations] = useState({
        uvLampLInch: '',
        uvLampLCm: '',
        t2LA: '',
        eWCm2: '',
        eMWcm2: '',
        exposureTimeTs: '',
        doseEt: '',
        totalIntensityMWcm2: '',
        doseJm2: '',
        noOfLmps: '',
        correctedUVD: '',
        correctedUVDx001: '',
        minimumInt: '',
        maximumInt: '',
        avgInt: ''
    });

    const [conversions, setConversions] = useState({
        coilWMmInCm: '',
        coilHMmInCm: '',
        uvLampLMmInCm: '',
        distInM: ''
    });

    const [includeDiagrams, setIncludeDiagrams] = useState('yes');
    const [reportType, setReportType] = useState('full');

    const uvCanvasRef = useRef(null);

    useEffect(() => {
        if (formData.coilWMm) {
            setConversions(prev => ({ ...prev, coilWMmInCm: (parseFloat(formData.coilWMm) / 10).toFixed(1) }));
        }
    }, [formData.coilWMm]);

    useEffect(() => {
        if (formData.coilHMm) {
            setConversions(prev => ({ ...prev, coilHMmInCm: (parseFloat(formData.coilHMm) / 10).toFixed(1) }));
        }
    }, [formData.coilHMm]);

    useEffect(() => {
        if (formData.uvLampLMm) {
            setConversions(prev => ({ ...prev, uvLampLMmInCm: (parseFloat(formData.uvLampLMm) / 10).toFixed(1) }));
        }
    }, [formData.uvLampLMm]);

    useEffect(() => {
        if (formData.distance) {
            setConversions(prev => ({ ...prev, distInM: (parseFloat(formData.distance) * 0.01).toFixed(2) }));
        }
    }, [formData.distance]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleReset = () => {
        setFormData({
            clientName: '',
            coilWMm: '',
            coilHMm: '',
            uvLampLMm: '',
            distance: '',
            airflowVolCfm: '',
            numRows: '',
            numCols: '',
            lampCount: '',
            totalWattage: '',
            rowWattage: '',
            intenAt1mMWcm2: '',
            uvWattageW: '',
            noOfLamps: '',
            distBtwnLamp: ''
        });
        setCalculations({
            uvLampLInch: '',
            uvLampLCm: '',
            t2LA: '',
            eWCm2: '',
            eMWcm2: '',
            exposureTimeTs: '',
            doseEt: '',
            totalIntensityMWcm2: '',
            doseJm2: '',
            noOfLmps: '',
            correctedUVD: '',
            correctedUVDx001: '',
            minimumInt: '',
            maximumInt: '',
            avgInt: ''
        });
        setConversions({
            coilWMmInCm: '',
            coilHMmInCm: '',
            uvLampLMmInCm: '',
            distInM: ''
        });
    };

    const calculate = () => {
        const {
            airflowVolCfm, coilWMm, coilHMm, uvLampLMm, uvWattageW,
            noOfLamps, numRows, numCols, rowWattage, lampCount,
            totalWattage, distance, distBtwnLamp
        } = formData;

        if (!airflowVolCfm || !coilWMm || !coilHMm) {
            alert('Please fill in all required fields');
            return;
        }

        const uvLampLInch = parseFloat(uvLampLMm) / 25.4;
        const uvLampLCm = parseFloat(uvLampLMm) / 10;
        const t2LA = 6.28 * uvLampLCm * parseFloat(distance);
        const eWCm2 = parseFloat(uvWattageW) / t2LA;
        const eMWcm2 = eWCm2 * 1000000;

        // Calculate exposure time
        const distanceInM = parseFloat(distance) * 0.01;
        const coilAreaMm2 = parseFloat(coilWMm) * parseFloat(coilHMm);
        const coilAreaM2 = coilAreaMm2 / 1000000;
        const valFlowRateM3s = parseFloat(airflowVolCfm) * 0.000471947;
        const flowVelMs = valFlowRateM3s / coilAreaM2;
        const exposureTimeTs = distanceInM / flowVelMs;

        const doseEt = eMWcm2 * exposureTimeTs;
        const totalIntensityMWcm2 = parseFloat(noOfLamps) * eMWcm2;
        const doseJm2 = doseEt * 0.01;
        const noOfLmps = parseFloat(coilHMm) / parseFloat(distBtwnLamp);

        // Calculation 2
        const lengthInCm = parseFloat(coilHMm) / 10;
        const widthInCm = parseFloat(coilWMm) / 10;
        const uvLampLMm1 = parseFloat(uvLampLMm) / 10;

        const totalAverageIntensity = ((parseFloat(totalWattage) * 0.3) * (Math.log(parseFloat(distance) / 2) + 0.577) * (10 ** 6)) / (2 * Math.PI * widthInCm * (parseFloat(distance) / 2));
        const contactTime = (((parseFloat(distance) / 30.48) * 1.5 * (lengthInCm / 30.48) * (widthInCm / 30.48)) / parseFloat(airflowVolCfm)) * 60;
        const correctedDosage = totalAverageIntensity * contactTime * 0.7;

        const diagonalDistance = Math.sqrt((lengthInCm / (parseFloat(numRows) * 2)) ** 2 + parseFloat(distance) ** 2);
        const minIntensity = ((parseFloat(rowWattage) * 0.3 * 10 ** 6) / (2 * Math.PI * uvLampLMm1 * diagonalDistance));

        let maxIntensity = ((parseFloat(rowWattage) * 0.3 * 10 ** 6) / (2 * Math.PI * uvLampLMm1 * parseFloat(distance)));

        if (parseFloat(numRows) >= 3) {
            maxIntensity += minIntensity * 2;
        }

        const avgIntensity = ((minIntensity * 2) + (parseFloat(numRows) * maxIntensity) + (parseFloat(numRows) - 1) * (minIntensity * 2)) / (parseFloat(numRows) * 2 + 1);

        setCalculations({
            uvLampLInch: uvLampLInch.toFixed(2),
            uvLampLCm: uvLampLCm.toFixed(2),
            t2LA: t2LA.toFixed(2),
            eWCm2: eWCm2.toFixed(4),
            eMWcm2: eMWcm2.toFixed(2),
            exposureTimeTs: exposureTimeTs.toFixed(4),
            doseEt: doseEt.toFixed(2),
            totalIntensityMWcm2: totalIntensityMWcm2.toFixed(2),
            doseJm2: doseJm2.toFixed(3),
            noOfLmps: noOfLmps.toFixed(2),
            correctedUVD: parseInt(correctedDosage).toFixed(0),
            correctedUVDx001: (correctedDosage * 0.01).toFixed(2),
            minimumInt: parseInt(minIntensity).toFixed(0),
            maximumInt: parseInt(maxIntensity).toFixed(0),
            avgInt: parseInt(avgIntensity).toFixed(0)
        });

        setTimeout(() => {
            generateGradientImage(
                parseFloat(coilWMm),
                parseFloat(coilHMm),
                parseInt(numCols),
                parseInt(numRows),
                parseFloat(distBtwnLamp),
                parseInt(noOfLamps),
                parseInt(minIntensity),
                parseInt(maxIntensity),
                parseInt(avgIntensity)
            );
        }, 100);
    };

    const generateGradientImage = (
        coilWMm,
        coilHMm,
        numCols,
        numRows,
        distBtwnLamp,
        noOfLamps,
        minInten,
        maxInten,
        avgInten
    ) => {
        coilWMm = coilWMm || 2440;
        coilHMm = coilHMm || 1520;
        numCols = Math.max(1, numCols || 2);
        numRows = Math.max(1, numRows || 3);
        distBtwnLamp = distBtwnLamp || 200;
        noOfLamps = Math.max(1, noOfLamps || (numCols * numRows));

        minInten = !isNaN(minInten) ? minInten : 100;
        maxInten = !isNaN(maxInten) ? maxInten : 2000;
        avgInten = !isNaN(avgInten) ? avgInten : (minInten + maxInten) / 2;

        const coilWCm = coilWMm / 10;
        const coilHCm = coilHMm / 10;
        const coilAspect = coilWCm / coilHCm;

        const canvas = uvCanvasRef.current;
        if (!canvas) return;

        const targetW = 600;
        const targetH = 400;
        const ratio = window.devicePixelRatio || 1;

        canvas.width = targetW * ratio;
        canvas.height = targetH * ratio;
        canvas.style.width = targetW + 'px';
        canvas.style.height = targetH + 'px';

        const ctx = canvas.getContext('2d');
        ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
        ctx.clearRect(0, 0, targetW, targetH);

        const marginLeft = 60, marginTop = 40, marginRight = 90, marginBottom = 60;
        const maxDrawW = targetW - marginLeft - marginRight;
        const maxDrawH = targetH - marginTop - marginBottom;

        let drawW, drawH;
        
        // FIXED: Improved aspect ratio handling to prevent distortion
        const availableAspect = maxDrawW / maxDrawH;
        
        if (coilAspect > availableAspect) {
            // Coil is wider relative to available space - constrain by width
            drawW = maxDrawW;
            drawH = drawW / coilAspect;
        } else {
            // Coil is taller relative to available space - constrain by height
            drawH = maxDrawH;
            drawW = drawH * coilAspect;
        }
        
        // Ensure we don't exceed bounds in either dimension
        if (drawW > maxDrawW) {
            drawW = maxDrawW;
            drawH = drawW / coilAspect;
        }
        if (drawH > maxDrawH) {
            drawH = maxDrawH;
            drawW = drawH * coilAspect;
        }

        const offsetX = marginLeft + (maxDrawW - drawW) / 2;
        const offsetY = marginTop + (maxDrawH - drawH) / 2;

        const usableW = drawW;
        const usableH = drawH;
        const colGap = usableW / numCols;
        const rowGap = usableH / numRows;

        const allSlots = [];
        for (let r = 0; r < numRows; r++) {
            for (let c = 0; c < numCols; c++) {
                const x = marginLeft + c * colGap + colGap / 2;
                const y = marginTop + r * rowGap + rowGap / 2;
                allSlots.push({ x, y, r, c });
            }
        }

        const pickEvenSlots = (slots, count) => {
            if (count >= slots.length) return slots.slice(0, count);
            const picked = [];
            const cx = marginLeft + drawW / 2, cy = marginTop + drawH / 2;
            const sorted = slots.slice().map(s => {
                const dx = s.x - cx, dy = s.y - cy;
                return { s, d: Math.sqrt(dx * dx + dy * dy) };
            }).sort((a, b) => a.d - b.d);
            for (let i = 0; i < count; i++) {
                const idx = Math.round(i * (sorted.length - 1) / (count - 1 || 1));
                picked.push(sorted[idx].s);
            }
            return picked;
        };

        const rods = pickEvenSlots(allSlots, Math.min(noOfLamps, allSlots.length));

        const gridW = 220;
        const gridH = 220;
        const field = new Float32Array(gridW * gridH);
        let maxVal = 0;

        const centerX = marginLeft + drawW / 2;
        const centerY = marginTop + drawH / 2;
        const radius = Math.sqrt((drawW / 2) ** 2 + (drawH / 2) ** 2);

        const radialBaseNorm = (px, py) => {
            const dx = px - centerX;
            const dy = py - centerY;
            const d = Math.sqrt(dx * dx + dy * dy) / radius;
            return Math.max(0, 1 - d);
        };

        const mapNormToIntensity = (vNorm) => {
            const t = vNorm;
            if (t >= 0.5) {
                const tt = (t - 0.5) / 0.5;
                return avgInten + (maxInten - avgInten) * easeOutQuad(tt);
            } else {
                const tt = t / 0.5;
                return minInten + (avgInten - minInten) * easeOutQuad(tt);
            }
        };

        const easeOutQuad = (t) => 1 - (1 - t) * (1 - t);

        const rodScales = rods.map(rd => {
            const dx = rd.x - centerX, dy = rd.y - centerY;
            const dd = Math.sqrt(dx * dx + dy * dy) / radius;
            return 1 - dd;
        });

        for (let gy = 0; gy < gridH; gy++) {
            for (let gx = 0; gx < gridW; gx++) {
                const px = marginLeft + (gx / (gridW - 1)) * drawW;
                const py = marginTop + (gy / (gridH - 1)) * drawH;

                const rNorm = radialBaseNorm(px, py);
                let val = mapNormToIntensity(rNorm);

                let add = 0;
                for (let ri = 0; ri < rods.length; ri++) {
                    const rd = rods[ri];
                    const dx = px - rd.x, dy = py - rd.y;
                    const dist = Math.sqrt(dx * dx + dy * dy);
                    const sigma = Math.max(1, Math.min(drawH, Math.max(colGap, rowGap)) * 0.35);
                    const rodPosFactor = rodScales[ri];
                    const rodBase = avgInten + (maxInten - avgInten) * rodPosFactor;
                    const g = Math.exp(-(dist * dist) / (2 * sigma * sigma));
                    add += rodBase * g;
                }

                const total = val + add;
                field[gy * gridW + gx] = total;
                if (total > maxVal) maxVal = total;
            }
        }

        ctx.save();
        ctx.beginPath();
        ctx.rect(marginLeft, marginTop, drawW, drawH);
        ctx.clip();

        ctx.fillStyle = '#43fffa';
        ctx.fillRect(marginLeft, marginTop, drawW, drawH);

        const imgW = Math.round(drawW);
        const imgH = Math.round(drawH);
        const img = ctx.createImageData(imgW, imgH);
        const data = img.data;

        const uvColorMap = (v) => {
            v = Math.max(0, Math.min(1, v));
            const stops = [
                { t: 0.00, c: [8, 30, 80] },
                { t: 0.20, c: [16, 64, 140] },
                { t: 0.40, c: [46, 107, 218] },
                { t: 0.60, c: [255, 233, 20] },
                { t: 0.80, c: [254, 164, 83] },
                { t: 1.00, c: [255, 120, 0] }
            ];

            for (let i = 0; i < stops.length - 1; i++) {
                const a = stops[i], b = stops[i + 1];
                if (v >= a.t && v <= b.t) {
                    const t = (v - a.t) / (b.t - a.t);
                    return [
                        Math.round(a.c[0] + (b.c[0] - a.c[0]) * t),
                        Math.round(a.c[1] + (b.c[1] - a.c[1]) * t),
                        Math.round(a.c[2] + (b.c[2] - a.c[2]) * t)
                    ];
                }
            }
            return stops[stops.length - 1].c.slice();
        };

        for (let y = 0; y < imgH; y++) {
            for (let x = 0; x < imgW; x++) {
                const gx = Math.floor(x / imgW * gridW);
                const gy = Math.floor(y / imgH * gridH);
                const v = field[gy * gridW + gx];
                const norm = maxVal > 0 ? Math.min(1, v / maxVal) : 0;
                const col = uvColorMap(norm);
                const idx = (y * imgW + x) * 4;
                data[idx] = col[0];
                data[idx + 1] = col[1];
                data[idx + 2] = col[2];
                data[idx + 3] = 255;
            }
        }

        const temp = document.createElement('canvas');
        temp.width = imgW;
        temp.height = imgH;
        temp.getContext('2d').putImageData(img, 0, 0);
        ctx.drawImage(temp, marginLeft, marginTop, drawW, drawH);

        ctx.restore();

        // Rod glows
        ctx.save();
        ctx.beginPath();
        ctx.rect(marginLeft, marginTop, drawW, drawH);
        ctx.clip();

        ctx.globalCompositeOperation = 'lighter';
        rods.forEach((rd, idx) => {
            const scale = rodScales[idx];
            const rodIntensity = avgInten + (maxInten - avgInten) * scale;
            const rodNorm = (rodIntensity - minInten) / Math.max(1, (maxInten - minInten));
            const glowRadius = Math.max(12, Math.min(drawW, drawH) * 0.18 * (0.6 + rodNorm * 0.8));

            const g = ctx.createRadialGradient(rd.x, rd.y, 0, rd.x, rd.y, glowRadius);
            const cHot = uvColorMap(1.0);
            const cMid = uvColorMap(Math.max(0, rodNorm * 0.8 + 0.15));
            const cCool = uvColorMap(Math.max(0.05, rodNorm * 0.3));

            g.addColorStop(0.00, `rgba(${cHot[0]},${cHot[1]},${cHot[2]},0.95)`);
            g.addColorStop(0.25, `rgba(${cMid[0]},${cMid[1]},${cMid[2]},0.65)`);
            g.addColorStop(0.60, `rgba(${cCool[0]},${cCool[1]},${cCool[2]},0.30)`);
            g.addColorStop(1.00, 'rgba(20,40,120,0.08)');

            ctx.fillStyle = g;
            ctx.beginPath();
            ctx.arc(rd.x, rd.y, glowRadius, 0, Math.PI * 2);
            ctx.fill();
        });

        ctx.globalCompositeOperation = 'source-over';
        ctx.restore();

        // Draw rods
        ctx.fillStyle = '#43fffa';
        const rodThickness = Math.max(4, Math.round(drawH * 0.02));
        const rodRectPad = Math.min(20, Math.round(colGap * 0.12));
        for (let i = 0; i < rods.length; i++) {
            const rd = rods[i];
            const rectW = Math.max(30, Math.round(colGap - rodRectPad * 2));
            ctx.fillRect(rd.x - rectW / 2, rd.y - rodThickness / 2, rectW, rodThickness);
            ctx.strokeStyle = 'rgba(0,0,0,0.12)';
            ctx.strokeRect(rd.x - rectW / 2, rd.y - rodThickness / 2, rectW, rodThickness);
        }

        // Border
        ctx.strokeStyle = 'rgba(0,0,0,0.18)';
        ctx.lineWidth = 1;
        ctx.strokeRect(marginLeft, marginTop, drawW, drawH);

        // Legend
        const barX = marginLeft + drawW + 18, barY = marginTop, barW = 20, barH = drawH;
        for (let i = 0; i < barH; i++) {
            const v = 1 - i / barH;
            const col = uvColorMap(v);
            ctx.fillStyle = `rgb(${col[0]},${col[1]},${col[2]})`;
            ctx.fillRect(barX, barY + i, barW, 1);
        }
        ctx.strokeStyle = '#000';
        ctx.strokeRect(barX, barY, barW, barH);
        ctx.fillStyle = '#000';
        ctx.font = '12px Arial';
        ctx.fillText('High', barX + barW + 6, barY + 12);
        ctx.fillText('Low', barX + barW + 6, barY + barH - 6);

        // Axes
        ctx.strokeStyle = '#000';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(marginLeft, marginTop + drawH);
        ctx.lineTo(marginLeft + drawW, marginTop + drawH);
        ctx.stroke();

        ctx.beginPath();
        ctx.moveTo(marginLeft, marginTop);
        ctx.lineTo(marginLeft, marginTop + drawH);
        ctx.stroke();

        const maxTicksX = 10;
        const ticksX = Math.min(maxTicksX, Math.max(2, Math.round(coilWCm / 10)));
        ctx.fillStyle = '#000';
        ctx.font = '10px Arial';
        for (let i = 0; i <= ticksX; i++) {
            const x = marginLeft + (i / ticksX) * drawW;
            const label = Math.round((i / ticksX) * coilWCm).toString();
            ctx.beginPath();
            ctx.moveTo(x, marginTop + drawH);
            ctx.lineTo(x, marginTop + drawH + 5);
            ctx.stroke();
            ctx.fillText(label, x - 8, marginTop + drawH + 18);
        }
        ctx.font = '12px Arial';
        ctx.fillText('Width (cm)', marginLeft + drawW / 2 - 30, marginTop + drawH + 36);

        const maxTicksY = 10;
        const ticksY = Math.min(maxTicksY, Math.max(2, Math.round(coilHCm / 10)));
        for (let i = 0; i <= ticksY; i++) {
            const y = marginTop + drawH - (i / ticksY) * drawH;
            const label = Math.round((i / ticksY) * coilHCm).toString();
            ctx.beginPath();
            ctx.moveTo(marginLeft, y);
            ctx.lineTo(marginLeft - 5, y);
            ctx.stroke();
            ctx.fillText(label, marginLeft - 34, y + 3);
        }

        ctx.save();
        ctx.translate(20, marginTop + drawH / 2);
        ctx.rotate(-Math.PI / 2);
        ctx.font = '12px Arial';
        ctx.fillText('Height (cm)', -40, 0);
        ctx.restore();

        ctx.fillStyle = '#000';
        ctx.font = 'bold 14px Arial';
        ctx.fillText(`Coil: ${Math.round(coilWCm)} cm × ${Math.round(coilHCm)} cm`, marginLeft + drawW / 2 - 70, marginTop - 15);
    };
    // Convert canvas to image (base64)
    const canvasToImage = (canvasRef) => {
        if (!canvasRef?.current) return "";
        return canvasRef.current.toDataURL("image/png", 1.0);
    };

    const buildAhuPdfHtml = () => {
        const showCalc1 = reportType === "full";
        const showDiagrams = includeDiagrams === "yes";
        const heatmapImg = showDiagrams ? canvasToImage(uvCanvasRef) : "";

        const v = (x) => (x !== undefined && x !== null && x !== "" ? x : "");

        return `
<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8">
<title>AHU-Results</title>
<link href="https://fonts.googleapis.com/css2?family=Tinos:wght@400;700&display=swap" rel="stylesheet">

<style>
 body {
    margin: 0;
    padding: 0;
    background: #fff;
    font-family: "Tinos", serif;
  }

  .page {
    width: 744px;
    margin: auto;
    height:800px;
    padding: 5px 14px;
    border: 3px solid #000;
    box-sizing: border-box;
    margin-top:30px;
    margin-bottom:40px;
  }


  .title {
    text-align: center;
    font-weight: bold;
    font-size: 15px;
    margin-bottom: 6px;
  }

  .columns {
    display: flex;
    font-size: 12px;
    line-height: 1.25;
    margin: 20px 0;
  }

  .left, .right {
    width: 50%;
  }

  .row {
    margin-bottom: 4px;
    white-space: nowrap;
  }

  .label {
    display: inline-block;
    width: 235px;
    font-weight: bold;
  }

  .divider {
    border-top: 1px solid #aaa;
    margin: 8px 0;
  }

  .results p {
    margin: 2px 0;
    font-size: 12px;
  }

  .diagrams {
    display: flex;
    gap: 25px;
    margin-top: 20px;
    margin:auto;
    justify-content: center;
    margin-top:40px;
  }

  .diagrams img {
    width: 60%;
  }

    .pdf-footer {
    margin-top: 6px;
    margin-bottom: 6px;
    padding: 10px 12px;
    text-align: center;
    color: #ffffff;
    font-family: Arial, Helvetica, sans-serif;
    background: linear-gradient(
      to right,
      #1aa7c9 0%,
      #5bb6a6 50%,
      #8bc34a 100%
    );
  }

  .footer-title {
    font-size: 17px;
    margin-bottom: 4px;
  }

  .footer-text {
    font-size: 10.5px;
    line-height: 1.35;
    margin-bottom: 4px;
  }

  .footer-contact {
    font-size: 10.5px;
    margin-bottom: 15px;
  }
</style>
</head>

<body>

<div style="display:flex;justify-content:end;padding-bottom:6px;">
  <img src="/logo.svg" style="height:60px"/>
</div>

<div class="page">
  <div class="title">AHU-Results</div>

  <div class="columns">

    <!-- INPUT PARAMETERS -->
    <div class="left">
      <div class="row"><span class="label">Client Name</span>: ${v(formData.clientName)}</div>
      <div class="row"><span class="label">Coil Width</span>: ${v(formData.coilWMm)} mm</div>
      <div class="row"><span class="label">Coil Height</span>: ${v(formData.coilHMm)} mm</div>
      <div class="row"><span class="label">Lamp Length</span>: ${v(formData.uvLampLMm)} mm</div>
      <div class="row"><span class="label">Distance</span>: ${v(formData.distance)} cm</div>
      <div class="row"><span class="label">Flow Rate</span>: ${v(formData.airflowVolCfm)} cfm</div>
      <div class="row"><span class="label">Number of Rows</span>: ${v(formData.numRows)} Nos</div>
      <div class="row"><span class="label">Number of Columns</span>: ${v(formData.numCols)} Nos</div>
      <div class="row"><span class="label">Total Count</span>: ${v(formData.lampCount)} Nos</div>
      <div class="row"><span class="label">Total Lamp Wattage</span>: ${v(formData.totalWattage)} Watts</div>
      <div class="row"><span class="label">Row Lamp Wattage</span>: ${v(formData.rowWattage)} Watts</div>
    </div>

    <!-- CALCULATION 1 -->
    ${showCalc1 ? `
    <div class="right">
      <div class="row"><span class="label">UV Lamp (L) (cm)</span>: ${v(calculations.uvLampLCm)}</div>
      <div class="row"><span class="label">2PiLA</span>: ${v(calculations.t2LA)}</div>
      <div class="row"><span class="label">E= Phi/2PiLA (W/cm2)</span>: ${v(calculations.eWCm2)}</div>
      <div class="row"><span class="label">E (µW/cm2)</span>: ${v(calculations.eMWcm2)}</div>
      <div class="row"><span class="label">Exposure time t (s)</span>: ${v(calculations.exposureTimeTs)}</div>
      <div class="row"><span class="label">Dose= E*t (µW.s/cm2)</span>: ${v(calculations.doseEt)}</div>
      <div class="row"><span class="label">Total intensity (µW/cm2)</span>: ${v(calculations.totalIntensityMWcm2)}</div>
    </div>
    ` : ``}

  </div>

  <div class="divider"></div>

  <!-- RESULTS -->
  <div class="results">
    <div class="title" style="text-decoration:underline; text-align:left">Simulation Results</div>
    <p>Your Corrected UV Dosage is <b>${v(calculations.correctedUVD)}</b> microwatt/ sq.cm./sec.</p>
    <p>Your Corrected UV Dosage in J/m2 <b>${v(calculations.correctedUVDx001)}</b>.</p>
    <p>Your Minimum Intensity is <b>${v(calculations.minimumInt)}</b> microwatt/ sq.cm.</p>
    <p>Your Maximum Intensity is <b>${v(calculations.maximumInt)}</b> microwatt/ sq.cm.</p>
    <p>Your Average Intensity is <b>${v(calculations.avgInt)}</b> microwatt/ sq.cm.</p>
  </div>

  ${showDiagrams ? `
  <div class="diagrams">
    <img src="${heatmapImg}">
  </div>
  ` : ""}

</div>

<div class="pdf-footer">
  <div class="footer-title">ULTRAPURE ENVIROCARE PRIVATE LIMITED</div>
  <div class="footer-text">
    Head Office: 3rd Floor, Ion House, Dr. E. Mose Road, Mahalakshmi, Mumbai-400011<br>
    Registered Office: CB-344, 2nd Floor, Ring Road, Naraina, New Delhi-110028
  </div>
  <div class="footer-contact">
    info@ultrapureindia.in | www.ultrapureindia.in | 1800-1100-50
  </div>
</div>

</body>
</html>
`;
    };





    const handleDownloadPDF = () => {
        const html = buildAhuPdfHtml();
        const el = document.createElement("div");
        el.innerHTML = html;
        document.body.appendChild(el);

        html2pdf()
            .set({
                filename: `AHU-Results-${reportType}-${includeDiagrams}.pdf`,
                margin: 6,
                image: { type: "jpeg", quality: 0.98 },
                html2canvas: { scale: 2 },
                jsPDF: { unit: "mm", format: "a4" }
            })
            .from(el)
            .save()
            .then(() => document.body.removeChild(el));
    };


    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <nav className="bg-white shadow-sm border-b">

                <div className="container mx-auto px-6 py-4 flex justify-between items-center">
                    <h1 className="text-xl font-semibold text-blue-700">
                        AHU Calculator
                    </h1>
                    <Link to="/duct_cal" className="text-gray-600 hover:text-gray-800">
                        <button className="px-4 py-2 border rounded hover:bg-gray-50">
                            Duct calculator
                        </button>
                    </Link>
                </div>
            </nav>

            {/* Main Content */}
            <div className="container mx-auto px-4 py-6">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Left Column - Input Form */}
                    <div className="lg:col-span-1">
                        <div className="bg-white rounded-lg shadow">
                            <div className="bg-blue-50 px-6 py-4 border-b">
                                <h3 className="text-lg font-semibold text-blue-700">User Input</h3>
                            </div>
                            <div className="p-6">
                                <form className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Client Name</label>
                                        <input
                                            type="text"
                                            name="clientName"
                                            value={formData.clientName}
                                            onChange={handleInputChange}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Coil (W) [mm]</label>
                                        <input
                                            type="text"
                                            name="coilWMm"
                                            value={formData.coilWMm}
                                            onChange={handleInputChange}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                        <span className="text-xs text-gray-500">in cm: {conversions.coilWMmInCm}</span>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Coil (H) [mm]</label>
                                        <input
                                            type="text"
                                            name="coilHMm"
                                            value={formData.coilHMm}
                                            onChange={handleInputChange}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                        <span className="text-xs text-gray-500">in cm: {conversions.coilHMmInCm}</span>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">UV LAMP (L) [mm]</label>
                                        <input
                                            type="text"
                                            name="uvLampLMm"
                                            value={formData.uvLampLMm}
                                            onChange={handleInputChange}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                        <span className="text-xs text-gray-500">in cm: {conversions.uvLampLMmInCm}</span>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Lamp Coil Distance</label>
                                        <input
                                            type="text"
                                            name="distance"
                                            value={formData.distance}
                                            onChange={handleInputChange}
                                            placeholder="in cm"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                        <span className="text-xs text-gray-500">in m: {conversions.distInM}</span>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Airflow Vol. (CFM)</label>
                                        <input
                                            type="text"
                                            name="airflowVolCfm"
                                            value={formData.airflowVolCfm}
                                            onChange={handleInputChange}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Number of Rows</label>
                                        <input
                                            type="text"
                                            name="numRows"
                                            value={formData.numRows}
                                            onChange={handleInputChange}
                                            placeholder="of lamps"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Number of Columns</label>
                                        <input
                                            type="text"
                                            name="numCols"
                                            value={formData.numCols}
                                            onChange={handleInputChange}
                                            placeholder="of lamps"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Total Count</label>
                                        <input
                                            type="text"
                                            name="lampCount"
                                            value={formData.lampCount}
                                            onChange={handleInputChange}
                                            placeholder="of lamps"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Total Lamp Wattage</label>
                                        <input
                                            type="text"
                                            name="totalWattage"
                                            value={formData.totalWattage}
                                            onChange={handleInputChange}
                                            placeholder="in W"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Row Lamp Wattage</label>
                                        <input
                                            type="text"
                                            name="rowWattage"
                                            value={formData.rowWattage}
                                            onChange={handleInputChange}
                                            placeholder="in W"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Intensity at 10 inch (µW/cm²)</label>
                                        <input
                                            type="text"
                                            name="intenAt1mMWcm2"
                                            value={formData.intenAt1mMWcm2}
                                            onChange={handleInputChange}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">UV wattage (W), Φ</label>
                                        <input
                                            type="text"
                                            name="uvWattageW"
                                            value={formData.uvWattageW}
                                            onChange={handleInputChange}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">No. of lamps</label>
                                        <input
                                            type="text"
                                            name="noOfLamps"
                                            value={formData.noOfLamps}
                                            onChange={handleInputChange}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Distance between Lamp (mm)</label>
                                        <input
                                            type="text"
                                            name="distBtwnLamp"
                                            value={formData.distBtwnLamp}
                                            onChange={handleInputChange}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>

                                    <div className="flex gap-3 pt-4">
                                        <button
                                            type="button"
                                            onClick={handleReset}
                                            className="flex-1 px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition"
                                        >
                                            Return to selection
                                        </button>
                                        <button
                                            type="button"
                                            onClick={calculate}
                                            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition"
                                        >
                                            Submit
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>

                    {/* Right Column - Calculations */}
                    <div className="lg:col-span-2 space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Calculations 1 */}
                            <div className="bg-white rounded-lg shadow">
                                <div className="bg-green-50 px-6 py-4 border-b">
                                    <h3 className="text-lg font-semibold text-green-700">
                                        Calculations: <span className="text-red-600">1</span>
                                    </h3>
                                </div>
                                <div className="p-6">
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">UV LAMP (L) (inch)</label>
                                            <input
                                                type="text"
                                                value={calculations.uvLampLInch}
                                                readOnly
                                                className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-md"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">UV Lamp (L) (cm)</label>
                                            <input
                                                type="text"
                                                value={calculations.uvLampLCm}
                                                readOnly
                                                className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-md"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">2ΠLA</label>
                                            <input
                                                type="text"
                                                value={calculations.t2LA}
                                                readOnly
                                                className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-md"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">E= Φ/2ΠLA (W/cm²)</label>
                                            <input
                                                type="text"
                                                value={calculations.eWCm2}
                                                readOnly
                                                className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-md"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">E (µW/cm²)</label>
                                            <input
                                                type="text"
                                                value={calculations.eMWcm2}
                                                readOnly
                                                className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-md"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Exposure time t (s)</label>
                                            <input
                                                type="text"
                                                value={calculations.exposureTimeTs}
                                                readOnly
                                                className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-md"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Dose= E*t (µW.s/cm²)</label>
                                            <input
                                                type="text"
                                                value={calculations.doseEt}
                                                readOnly
                                                className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-md"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Total intensity (µW/cm²)</label>
                                            <input
                                                type="text"
                                                value={calculations.totalIntensityMWcm2}
                                                readOnly
                                                className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-md"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Dose (J/m²)</label>
                                            <input
                                                type="text"
                                                value={calculations.doseJm2}
                                                readOnly
                                                className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-md"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">No. of Lamps</label>
                                            <input
                                                type="text"
                                                value={calculations.noOfLmps}
                                                readOnly
                                                className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-md"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Calculations 2 */}
                            <div className="bg-white rounded-lg shadow">
                                <div className="bg-green-50 px-6 py-4 border-b">
                                    <h3 className="text-lg font-semibold text-green-700">
                                        Calculations: <span className="text-red-600">2</span>
                                    </h3>
                                </div>
                                <div className="p-6">
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Corrected UV Dosage <span className="text-gray-500">(in microwatt/sq.cm./sec)</span>
                                            </label>
                                            <input
                                                type="text"
                                                value={calculations.correctedUVD}
                                                readOnly
                                                className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-md"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Corrected UV Dosage X 0.01 <span className="text-gray-500">(J / Sq. meters)</span>
                                            </label>
                                            <input
                                                type="text"
                                                value={calculations.correctedUVDx001}
                                                readOnly
                                                className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-md"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Minimum Intensity <span className="text-gray-500">(in microwatt/sq.cm)</span>
                                            </label>
                                            <input
                                                type="text"
                                                value={calculations.minimumInt}
                                                readOnly
                                                className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-md"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Maximum Intensity <span className="text-gray-500">(in microwatt/sq.cm)</span>
                                            </label>
                                            <input
                                                type="text"
                                                value={calculations.maximumInt}
                                                readOnly
                                                className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-md"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Average Intensity <span className="text-gray-500">(in microwatt/sq.cm)</span>
                                            </label>
                                            <input
                                                type="text"
                                                value={calculations.avgInt}
                                                readOnly
                                                className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-md"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* PDF Download Section */}
                                <div className="border-t px-6 py-4">
                                    <div className="space-y-4">
                                        <label className="block text-sm font-semibold text-gray-700">PDF Download:</label>
                                        <hr className="my-3" />

                                        <div className="space-y-4">
                                            <div>
                                                <p className="text-sm font-semibold text-gray-700 mb-2">Full Report</p>
                                                <div className="grid grid-cols-2 gap-4">
                                                    <label className="flex items-center space-x-2 cursor-pointer">
                                                        <input
                                                            type="radio"
                                                            name="fullReport"
                                                            value="yes"
                                                            checked={reportType === 'full' && includeDiagrams === 'yes'}
                                                            onChange={() => {
                                                                setReportType('full');
                                                                setIncludeDiagrams('yes');
                                                            }}
                                                            className="w-4 h-4 text-blue-600"
                                                        />
                                                        <span className="text-sm">All <strong>With Diagrams</strong></span>
                                                    </label>
                                                    <label className="flex items-center space-x-2 cursor-pointer">
                                                        <input
                                                            type="radio"
                                                            name="fullReport"
                                                            value="no"
                                                            checked={reportType === 'full' && includeDiagrams === 'no'}
                                                            onChange={() => {
                                                                setReportType('full');
                                                                setIncludeDiagrams('no');
                                                            }}
                                                            className="w-4 h-4 text-blue-600"
                                                        />
                                                        <span className="text-sm">All <strong>Without Diagrams</strong></span>
                                                    </label>
                                                </div>
                                            </div>

                                            <div>
                                                <p className="text-sm font-semibold text-gray-700 mb-2">Without Calculation 1</p>
                                                <div className="grid grid-cols-2 gap-4">
                                                    <label className="flex items-center space-x-2 cursor-pointer">
                                                        <input
                                                            type="radio"
                                                            name="calc1Report"
                                                            value="yes"
                                                            checked={reportType === 'withoutCalc1' && includeDiagrams === 'yes'}
                                                            onChange={() => {
                                                                setReportType('withoutCalc1');
                                                                setIncludeDiagrams('yes');
                                                            }}
                                                            className="w-4 h-4 text-blue-600"
                                                        />
                                                        <span className="text-sm">With Diagrams</span>
                                                    </label>
                                                    <label className="flex items-center space-x-2 cursor-pointer">
                                                        <input
                                                            type="radio"
                                                            name="calc1Report"
                                                            value="no"
                                                            checked={reportType === 'withoutCalc1' && includeDiagrams === 'no'}
                                                            onChange={() => {
                                                                setReportType('withoutCalc1');
                                                                setIncludeDiagrams('no');
                                                            }}
                                                            className="w-4 h-4 text-blue-600"
                                                        />
                                                        <span className="text-sm">Without Diagrams</span>
                                                    </label>
                                                </div>
                                            </div>
                                        </div>

                                        <button
                                            onClick={handleDownloadPDF}
                                            className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition mt-4"
                                        >
                                            Download PDF
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Canvas Visualization */}
                        <div className="bg-white rounded-lg shadow p-6">
                            <canvas
                                ref={uvCanvasRef}
                                className="border border-gray-300 mx-auto"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AHUCalculator;